// ===================================================================
// KAIXO - CallManager
// ===================================================================
// Orquesta todo el sistema de llamadas. Se monta UNA sola vez en App.js.
//
// Arregla 5 bugs reportados por el usuario:
//  1) Sin ringtone → añade tono de llamada sintetizado con Web Audio
//     (no requiere asset mp3) + vibración repetida cada 2s mientras suena.
//  2) Video solo unidireccional → refactor del pipeline de pistas:
//     ahora guardamos pistas remotas en useState y usamos useEffect para
//     adjuntarlas a los elementos <video>/<audio> cuando el DOM esté
//     montado (antes track.attach fallaba cuando el evento llegaba antes
//     de que el componente re-renderizara con los refs listos).
//  3) Botón colgar mostraba una "X" (Icons.Close) rotada → sustituido
//     por un icono de teléfono con receptor colgando.
//  4) Latencia al aceptar + botón colgar aparece en el mismo lugar que
//     aceptar → ahora accept tiene state "connecting" que muestra
//     spinner y deshabilita el botón. El botón colgar se posiciona
//     separado para que no se pulse por error.
//  5) Flip cámara poco fiable → ahora llevamos facing en useState local
//     en vez de leer getSettings() (que devuelve undefined en algunos
//     navegadores/Android WebView).
// ===================================================================

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
    Room, RoomEvent, Track, VideoPresets,
    createLocalAudioTrack, createLocalVideoTrack
} from 'livekit-client';
import { Icons } from '../Icons/Icons';
import { getSocket } from '../../services/socket';
import {
    callsInitiate, callsAccept, callsDecline, callsCancel, callsEnd, callsGetConfig, callsInvite
} from '../../services/callsApi';
import { startAudioCapture } from './liveSubtitles';
import './CallManager.css';

// i18n local — strings críticos que ve el usuario durante una llamada.
// Cubre los 7 idiomas con más volumen + fallback a inglés. Mensajes
// internos / debug se mantienen en español (no son visibles).
const I18N = {
    español: {
        callInProgress: 'Ya hay una llamada en curso',
        callsUnavailable: 'Las llamadas no están disponibles en este momento',
        cantStartCall: 'No se pudo iniciar la llamada',
        cantAccept: 'No se pudo aceptar',
        cantSwitchCamera: 'No se pudo cambiar la cámara',
        userBusyTitle: (name) => `${name} está en otra llamada`,
        userBusySub: 'No se ha podido conectar. Inténtalo en unos minutos.',
        connecting: 'Conectando…',
        inviteTitle: 'Invitar a la llamada',
        searchContact: 'Buscar contacto...',
        loadingContacts: 'Cargando contactos…'
    },
    inglés: {
        callInProgress: 'A call is already in progress',
        callsUnavailable: 'Calls are unavailable right now',
        cantStartCall: 'Could not start the call',
        cantAccept: 'Could not accept',
        cantSwitchCamera: 'Could not switch camera',
        userBusyTitle: (name) => `${name} is on another call`,
        userBusySub: 'Could not connect. Try again in a few minutes.',
        connecting: 'Connecting…',
        inviteTitle: 'Invite to the call',
        searchContact: 'Search contact...',
        loadingContacts: 'Loading contacts…'
    },
    francés: {
        callInProgress: 'Un appel est déjà en cours',
        callsUnavailable: 'Les appels ne sont pas disponibles pour le moment',
        cantStartCall: "Impossible de démarrer l'appel",
        cantAccept: "Impossible d'accepter",
        cantSwitchCamera: 'Impossible de changer de caméra',
        userBusyTitle: (name) => `${name} est en pleine conversation`,
        userBusySub: 'Connexion impossible. Réessaie dans quelques minutes.',
        connecting: 'Connexion…',
        inviteTitle: "Inviter à l'appel",
        searchContact: 'Chercher contact...',
        loadingContacts: 'Chargement…'
    },
    alemán: {
        callInProgress: 'Es läuft bereits ein Anruf',
        callsUnavailable: 'Anrufe sind gerade nicht verfügbar',
        cantStartCall: 'Anruf konnte nicht gestartet werden',
        cantAccept: 'Konnte nicht annehmen',
        cantSwitchCamera: 'Kamera konnte nicht gewechselt werden',
        userBusyTitle: (name) => `${name} ist in einem anderen Gespräch`,
        userBusySub: 'Verbindung fehlgeschlagen. Versuche es in wenigen Minuten erneut.',
        connecting: 'Verbinden…',
        inviteTitle: 'Zum Anruf einladen',
        searchContact: 'Kontakt suchen...',
        loadingContacts: 'Kontakte werden geladen…'
    },
    portugués: {
        callInProgress: 'Já há uma chamada em curso',
        callsUnavailable: 'As chamadas não estão disponíveis neste momento',
        cantStartCall: 'Não foi possível iniciar a chamada',
        cantAccept: 'Não foi possível aceitar',
        cantSwitchCamera: 'Não foi possível mudar a câmara',
        userBusyTitle: (name) => `${name} está noutra chamada`,
        userBusySub: 'Não foi possível conectar. Tenta de novo em alguns minutos.',
        connecting: 'A ligar…',
        inviteTitle: 'Convidar para a chamada',
        searchContact: 'Pesquisar contacto...',
        loadingContacts: 'A carregar contactos…'
    },
    italiano: {
        callInProgress: "C'è già una chiamata in corso",
        callsUnavailable: 'Le chiamate non sono disponibili in questo momento',
        cantStartCall: 'Impossibile avviare la chiamata',
        cantAccept: 'Impossibile accettare',
        cantSwitchCamera: 'Impossibile cambiare fotocamera',
        userBusyTitle: (name) => `${name} è in un'altra chiamata`,
        userBusySub: 'Connessione non riuscita. Riprova fra qualche minuto.',
        connecting: 'Connessione…',
        inviteTitle: 'Invita alla chiamata',
        searchContact: 'Cerca contatto...',
        loadingContacts: 'Caricamento contatti…'
    },
    euskera: {
        callInProgress: 'Dei bat martxan dago jada',
        callsUnavailable: 'Deiak ez daude eskuragarri une honetan',
        cantStartCall: 'Ezin izan da deia hasi',
        cantAccept: 'Ezin izan da onartu',
        cantSwitchCamera: 'Ezin izan da kamera aldatu',
        userBusyTitle: (name) => `${name} beste dei batean dago`,
        userBusySub: 'Ezin izan da konektatu. Saiatu berriro minutu batzuetan.',
        connecting: 'Konektatzen…',
        inviteTitle: 'Deira gonbidatu',
        searchContact: 'Kontaktua bilatu...',
        loadingContacts: 'Kontaktuak kargatzen…'
    }
};
function pickCallL() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return I18N[u?.language] || I18N.inglés;
    } catch { return I18N.inglés; }
}

const S_IDLE = 'idle';
const S_OUTGOING = 'outgoing';
const S_INCOMING = 'incoming';
const S_CONNECTING = 'connecting'; // NUEVO: callee pulsó aceptar, conectando
const S_ACTIVE = 'active';

// Icono de teléfono colgado (receptor horizontal) — para el botón rojo
// de rechazar / colgar. No tiene rotación, es un SVG dibujado a medida.
const HangupIcon = ({ size = 28 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="white">
        <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.29-.7.29-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.18-.29-.43-.29-.71 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.66c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.29-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
    </svg>
);

// Pre-calentamos un AudioContext global al primer gesto del usuario
// (click/touch/keydown anywhere). La política de autoplay del navegador
// bloquea el AudioContext si se crea sin gesture — causa por la que la
// primera llamada entrante no sonaba: la pestaña se abrió, no hubo
// interacción, el ringtone se creaba pero quedaba suspended → silencio.
// Una vez que hay gesture se queda unlocked durante toda la sesión.
(function primeAudioContext() {
    if (typeof window === 'undefined') return;
    if (window.__kaixoAudioPrimed) return;
    const unlock = () => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;
            if (!window.__kaixoAudioCtx) {
                window.__kaixoAudioCtx = new AudioCtx();
            }
            // "resume" tras gesture lo pone en estado 'running'
            if (window.__kaixoAudioCtx.state === 'suspended') {
                window.__kaixoAudioCtx.resume().catch(() => {});
            }
            window.__kaixoAudioPrimed = true;
            // Una vez unlocked, quitamos listeners
            window.removeEventListener('click', unlock);
            window.removeEventListener('touchstart', unlock);
            window.removeEventListener('keydown', unlock);
        } catch {}
    };
    window.addEventListener('click', unlock, { once: false });
    window.addEventListener('touchstart', unlock, { once: false });
    window.addEventListener('keydown', unlock, { once: false });
})();

// Boost de volumen sobre el audio remoto durante la llamada. LiveKit
// reproduce el track con el volumen "natural" del navegador y en muchos
// móviles se oye bajo (canal Media en lugar de canal Llamada). Aplicamos
// un GainNode 1.6× sobre el elemento <audio> para subir percepción.
// Defensivo: si Web Audio falla por cualquier motivo (browser viejo,
// contexto bloqueado, doble createMediaElementSource), track.attach ya
// está hecho y el <audio> reproduce a volumen normal. Es un boost OPCIONAL,
// jamás bloquea el audio si algo va mal.
const REMOTE_VOLUME_BOOST = 1.6;
const _kaixoBoostedAudioEls = new WeakMap();
function applyRemoteVolumeBoost(audioEl) {
    if (!audioEl) return;
    if (_kaixoBoostedAudioEls.has(audioEl)) return; // ya wireado, no rewirear
    try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = window.__kaixoAudioCtx || new AudioCtx();
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const source = ctx.createMediaElementSource(audioEl);
        const gain = ctx.createGain();
        gain.gain.value = REMOTE_VOLUME_BOOST;
        source.connect(gain);
        gain.connect(ctx.destination);
        _kaixoBoostedAudioEls.set(audioEl, { ctx, gain });
    } catch (e) {
        console.warn('[CallManager] Boost de volumen no aplicado:', e?.message || e);
    }
}

// Ringtone sintetizado con Web Audio API. Dos tonos alternos cada ~1s
// en bucle. No requiere assets. Devuelve función para detenerlo.
// Volumen subido a 0.45 (antes 0.18) — los usuarios reportaban que se
// oía muy bajo en móviles con altavoz pequeño.
function playRingtone() {
    let ctx;
    try {
        // Reutilizamos el AudioContext global ya desbloqueado si existe
        ctx = window.__kaixoAudioCtx || new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }
    } catch { return () => {}; }

    let stopped = false;
    let timerId = null;

    const beep = (freq, durationMs, vol = 0.45) => {
        if (stopped) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        // Envelope suave (fade in/out) para que no suene tipo alarma
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(vol, now + 0.05);
        gain.gain.setValueAtTime(vol, now + durationMs / 1000 - 0.05);
        gain.gain.linearRampToValueAtTime(0, now + durationMs / 1000);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + durationMs / 1000);
    };

    const ringCycle = () => {
        if (stopped) return;
        // Patrón clásico: dos tonos cortos, pausa, repetir
        beep(480, 400);
        setTimeout(() => beep(620, 400), 500);
        timerId = setTimeout(ringCycle, 2000);
    };

    ringCycle();

    return () => {
        stopped = true;
        if (timerId) clearTimeout(timerId);
        // Solo cerramos si NO es el AudioContext global compartido
        if (ctx !== window.__kaixoAudioCtx) {
            try { ctx.close(); } catch {}
        }
    };
}

// Ringback (tono que oye el QUIEN LLAMA mientras espera respuesta).
// Pattern estándar telefónico "ring-ring" europeo: doble pulso de ~440Hz
// durante 1s, silencio 2s, repetir. Volumen moderado para no molestar.
function playRingback() {
    let ctx;
    try {
        ctx = window.__kaixoAudioCtx || new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }
    } catch { return () => {}; }

    let stopped = false;
    let timerId = null;

    const tone = (freq, durationMs, vol = 0.30) => {
        if (stopped) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(vol, now + 0.04);
        gain.gain.setValueAtTime(vol, now + durationMs / 1000 - 0.06);
        gain.gain.linearRampToValueAtTime(0, now + durationMs / 1000);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + durationMs / 1000);
    };

    const cycle = () => {
        if (stopped) return;
        // Doble tono ~1s + silencio ~2s
        tone(440, 400);
        setTimeout(() => tone(440, 400), 500);
        timerId = setTimeout(cycle, 3000);
    };

    cycle();

    return () => {
        stopped = true;
        if (timerId) clearTimeout(timerId);
        if (ctx !== window.__kaixoAudioCtx) {
            try { ctx.close(); } catch {}
        }
    };
}

// Tono de comunicando (busy / ocupado). Patrón estándar telefónico:
// pulsos cortos de ~480Hz alternando con silencios. Suena ~3 segundos.
// Devuelve función para detenerlo (en caso de que el caller cuelgue antes).
function playBusyTone(durationMs = 3000) {
    let ctx;
    try {
        ctx = window.__kaixoAudioCtx || new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch { return () => {}; }

    let stopped = false;
    const oscs = [];
    const start = ctx.currentTime;
    const beepLen = 0.5;   // 500 ms tono
    const gapLen = 0.5;    // 500 ms silencio
    const cycle = beepLen + gapLen;
    const cycles = Math.ceil((durationMs / 1000) / cycle);

    for (let i = 0; i < cycles; i++) {
        const t0 = start + i * cycle;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 480;  // tono típico de busy
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(0.22, t0 + 0.02);
        gain.gain.setValueAtTime(0.22, t0 + beepLen - 0.02);
        gain.gain.linearRampToValueAtTime(0, t0 + beepLen);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + beepLen);
        oscs.push(osc);
    }

    return () => {
        if (stopped) return;
        stopped = true;
        for (const o of oscs) {
            try { o.stop(); } catch {}
        }
    };
}

// Vibración repetida mientras suena (solo dispositivos que la soportan).
function startVibratePattern() {
    if (!navigator.vibrate) return () => {};
    let stopped = false;
    const cycle = () => {
        if (stopped) return;
        try { navigator.vibrate([600, 400, 600, 400, 600]); } catch {}
        setTimeout(cycle, 3000);
    };
    cycle();
    return () => { stopped = true; try { navigator.vibrate(0); } catch {} };
}

export default function CallManager({ currentUser }) {
    const [state, setState] = useState(S_IDLE);
    const [callInfo, setCallInfo] = useState(null);
    // Banner efímero "el usuario está en otra llamada" cuando intentamos
    // llamar a alguien ocupado. Se auto-cierra a los 4s.
    const [busyNotice, setBusyNotice] = useState(null);
    useEffect(() => {
        if (!busyNotice) return;
        const t = setTimeout(() => setBusyNotice(null), 4000);
        return () => clearTimeout(t);
    }, [busyNotice]);

    // Mensajes recibidos durante la llamada — se acumulan en un contador
    // visible en el modal para que al colgar el usuario sepa que tiene
    // chats que revisar. No abrimos los chats desde aquí; los unreadCount
    // de ConversationList ya están actualizados por su propio listener.
    const [missedMsgs, setMissedMsgs] = useState({ count: 0, lastFrom: '' });
    const [muted, setMuted] = useState(false);
    const [cameraOff, setCameraOff] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [enabled, setEnabled] = useState(false);
    // Pistas remotas actuales (una video + una audio). Guardarlas en
    // state permite que React adjunte a los <video>/<audio> cuando el
    // DOM esté listo, sin depender del timing del event TrackSubscribed.
    const [remoteVideoTrack, setRemoteVideoTrack] = useState(null);
    const [remoteAudioTrack, setRemoteAudioTrack] = useState(null);
    // Para llamadas grupales: mapa de participantes remotos. Por cada
    // identidad: { name, videoTrack, audioTrack }. Sigue creciendo y
    // shrinkándose vía eventos LiveKit (TrackSubscribed/Unsubscribed +
    // ParticipantConnected/Disconnected). En 1-a-1 solo tiene 1 entrada.
    const [remoteParticipants, setRemoteParticipants] = useState(new Map());
    const [localVideoTrack, setLocalVideoTrack] = useState(null);
    // Facing de la cámara — lo guardamos en state porque getSettings()
    // devuelve undefined en Android WebView.
    const [cameraFacing, setCameraFacing] = useState('user');

    const roomRef = useRef(null);
    const localVideoElRef = useRef(null);
    const remoteVideoElRef = useRef(null);
    const remoteAudioElRef = useRef(null);
    const elapsedTimerRef = useRef(null);
    const stopRingtoneRef = useRef(null);
    const stopRingbackRef = useRef(null);

    // Modal de "invitar a más personas durante la llamada"
    const [showInviteModal, setShowInviteModal] = useState(false);
    const stopVibrateRef = useRef(null);
    // Subtítulos traducidos en vivo. Mantenemos un buffer de las últimas
    // N frases — cada una con TTL individual — para que el usuario tenga
    // tiempo de leer aunque lleguen varias seguidas. Las más antiguas
    // van desapareciendo por arriba mientras las nuevas entran por abajo.
    const [subtitleOn, setSubtitleOn] = useState(true);
    const [subtitleLines, setSubtitleLines] = useState([]); // [{ id, text, speakerName, ts }]
    const audioCaptureRef = useRef(null);
    const subtitleLineIdRef = useRef(0);

    // ──────────────── Config inicial ───────────────────────────
    useEffect(() => {
        let cancelled = false;
        callsGetConfig()
            .then(({ data }) => { if (!cancelled) setEnabled(!!data?.enabled); })
            .catch(() => { if (!cancelled) setEnabled(false); });
        return () => { cancelled = true; };
    }, []);

    // ──────────────── Adjuntar pistas remotas al DOM ────────────
    // Cuando cambia la track remota O el element del DOM, hacemos attach.
    // Esto evita el bug #2 (video unidireccional): antes attach se
    // disparaba dentro del RoomEvent.TrackSubscribed, pero el <video>
    // ref era null si React aún no había re-renderizado con S_ACTIVE.
    useEffect(() => {
        const videoEl = remoteVideoElRef.current;
        if (remoteVideoTrack && videoEl) {
            remoteVideoTrack.attach(videoEl);
        }
        return () => {
            if (remoteVideoTrack && videoEl) {
                try { remoteVideoTrack.detach(videoEl); } catch {}
            }
        };
    }, [remoteVideoTrack, state]);

    useEffect(() => {
        const audioEl = remoteAudioElRef.current;
        if (remoteAudioTrack && audioEl) {
            remoteAudioTrack.attach(audioEl);
            applyRemoteVolumeBoost(audioEl);
        }
        return () => {
            if (remoteAudioTrack && audioEl) {
                try { remoteAudioTrack.detach(audioEl); } catch {}
            }
        };
    }, [remoteAudioTrack, state]);

    useEffect(() => {
        const videoEl = localVideoElRef.current;
        if (localVideoTrack && videoEl) {
            localVideoTrack.attach(videoEl);
        }
        return () => {
            if (localVideoTrack && videoEl) {
                try { localVideoTrack.detach(videoEl); } catch {}
            }
        };
    }, [localVideoTrack, state]);

    // ──────────────── Cleanup global ───────────────────────────
    const teardown = useCallback(() => {
        if (elapsedTimerRef.current) {
            clearInterval(elapsedTimerRef.current);
            elapsedTimerRef.current = null;
        }
        if (stopRingtoneRef.current) {
            try { stopRingtoneRef.current(); } catch {}
            stopRingtoneRef.current = null;
        }
        if (stopRingbackRef.current) {
            try { stopRingbackRef.current(); } catch {}
            stopRingbackRef.current = null;
        }
        if (stopVibrateRef.current) {
            try { stopVibrateRef.current(); } catch {}
            stopVibrateRef.current = null;
        }
        if (audioCaptureRef.current) {
            try { audioCaptureRef.current.stop(); } catch {}
            audioCaptureRef.current = null;
        }
        try {
            const socket = typeof getSocket === 'function' ? getSocket() : null;
            if (socket) socket.emit('call:subtitles:stop');
        } catch {}
        if (roomRef.current) {
            try { roomRef.current.disconnect(); } catch {}
            roomRef.current = null;
        }
        setElapsed(0);
        setMuted(false);
        setCameraOff(false);
        setRemoteVideoTrack(null);
        setRemoteAudioTrack(null);
        setRemoteParticipants(new Map());
        setLocalVideoTrack(null);
        setSubtitleLines([]);
    }, []);

    const resetToIdle = useCallback(() => {
        teardown();
        setState(S_IDLE);
        setCallInfo(null);
    }, [teardown]);

    // ──────────────── Conectar a LiveKit ───────────────────────
    // Nota: callId se pasa como parámetro (no leído del closure) porque
    // callInfo dentro del useCallback es stale — el hook se memoiza y su
    // scope no ve los setCallInfo posteriores.
    const connectToRoom = useCallback(async ({ url, token, type, callId }) => {
        const room = new Room({
            adaptiveStream: true,
            dynacast: true,
            videoCaptureDefaults: {
                resolution: VideoPresets.h720.resolution
            },
            publishDefaults: {
                videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
                videoEncoding: VideoPresets.h720.encoding,
                videoCodec: 'vp9'
            }
        });
        roomRef.current = room;

        // Cuando nos suscribimos a una pista remota, la guardamos en state.
        // El useEffect de attach se encarga del DOM cuando esté listo.
        // En 1-a-1 mantenemos los singulares remoteVideoTrack/remoteAudioTrack
        // (la UI clásica los usa). Para llamadas grupales también poblamos un
        // mapa por participante para renderizar el grid.
        room.on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
            const pid = participant?.identity || 'unknown';
            const pname = participant?.name || pid;
            setRemoteParticipants(prev => {
                const next = new Map(prev);
                const entry = next.get(pid) || { id: pid, name: pname, videoTrack: null, audioTrack: null };
                if (track.kind === Track.Kind.Video) entry.videoTrack = track;
                else if (track.kind === Track.Kind.Audio) entry.audioTrack = track;
                entry.name = pname;
                next.set(pid, entry);
                return next;
            });
            if (track.kind === Track.Kind.Video) {
                setRemoteVideoTrack(track);
            } else if (track.kind === Track.Kind.Audio) {
                setRemoteAudioTrack(track);
            }
        });

        room.on(RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
            const pid = participant?.identity || 'unknown';
            setRemoteParticipants(prev => {
                if (!prev.has(pid)) return prev;
                const next = new Map(prev);
                const entry = { ...next.get(pid) };
                if (track.kind === Track.Kind.Video) entry.videoTrack = null;
                else if (track.kind === Track.Kind.Audio) entry.audioTrack = null;
                if (!entry.videoTrack && !entry.audioTrack) next.delete(pid);
                else next.set(pid, entry);
                return next;
            });
            if (track.kind === Track.Kind.Video) setRemoteVideoTrack(null);
            else if (track.kind === Track.Kind.Audio) setRemoteAudioTrack(null);
            try { track.detach(); } catch {}
        });

        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
            const pid = participant?.identity;
            if (pid) {
                setRemoteParticipants(prev => {
                    if (!prev.has(pid)) return prev;
                    const next = new Map(prev);
                    next.delete(pid);
                    return next;
                });
            }
            // En 1-a-1, el otro se va = la llamada termina.
            // En grupal, solo terminamos si no queda NADIE más en la sala.
            const isGroup = !!callInfoRef.current?.isGroup;
            if (!isGroup) {
                resetToIdle();
            } else {
                // Comprobar si quedan otros participantes remotos en la sala
                const remaining = room.remoteParticipants ? room.remoteParticipants.size : 0;
                if (remaining === 0) resetToIdle();
            }
        });

        room.on(RoomEvent.Disconnected, () => {
            resetToIdle();
        });

        // Subtítulos: escuchar DataChannel. Dos tipos de eventos:
        //
        //  - subtitle_pending: el hablante empezó una frase. Mostramos una
        //    franja tenue con animación "traduciendo..." mientras el
        //    backend transcribe y traduce. El id se guarda para poder
        //    reemplazarla cuando llegue el texto real.
        //
        //  - subtitle: texto ya traducido. Si trae replacesId, sustituye la
        //    franja pendiente correspondiente; si no, añade línea nueva.
        room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
            if (topic && topic !== 'kaixo-subtitle') return;
            try {
                const text = new TextDecoder().decode(payload);
                const data = JSON.parse(text);
                const MAX_LINES = 3;
                const TTL_MS = 10000;
                const PENDING_TTL_MS = 6000;  // más corto — si no llega FINAL, quitamos

                if (data?.kind === 'subtitle_pending' && data.id) {
                    const line = {
                        id: data.id,        // usamos el id del backend para poder reemplazar
                        text: null,
                        pending: true,
                        speakerName: data.speakerName,
                        ts: Date.now()
                    };
                    setSubtitleLines(prev => {
                        // evitar duplicados si llega varias veces
                        if (prev.some(l => l.id === data.id)) return prev;
                        return [...prev, line].slice(-MAX_LINES);
                    });
                    setTimeout(() => {
                        // solo eliminar si sigue pendiente (no se resolvió)
                        setSubtitleLines(prev => prev.filter(l => !(l.id === data.id && l.pending)));
                    }, PENDING_TTL_MS);
                    return;
                }

                if (data?.kind === 'subtitle' && data.text) {
                    const replacesId = data.replacesId;
                    if (replacesId) {
                        // Reemplazar la línea pending que creamos antes
                        let replaced = false;
                        setSubtitleLines(prev => {
                            const next = prev.map(l => {
                                if (l.id === replacesId) {
                                    replaced = true;
                                    return {
                                        ...l,
                                        text: data.text,
                                        pending: false,
                                        ts: Date.now()
                                    };
                                }
                                return l;
                            });
                            // Si por algún motivo el pending ya había caducado,
                            // lo añadimos como línea nueva al final.
                            if (!replaced) {
                                next.push({
                                    id: `s-${++subtitleLineIdRef.current}`,
                                    text: data.text,
                                    pending: false,
                                    speakerName: data.speakerName,
                                    ts: Date.now()
                                });
                            }
                            return next.slice(-MAX_LINES);
                        });
                        // timer de TTL normal desde ahora
                        setTimeout(() => {
                            setSubtitleLines(prev => prev.filter(l => l.id !== replacesId));
                        }, TTL_MS);
                    } else {
                        // Sin replacesId — línea nueva directa
                        const lineId = `s-${++subtitleLineIdRef.current}`;
                        const line = {
                            id: lineId,
                            text: data.text,
                            pending: false,
                            speakerName: data.speakerName,
                            ts: Date.now()
                        };
                        setSubtitleLines(prev => [...prev, line].slice(-MAX_LINES));
                        setTimeout(() => {
                            setSubtitleLines(prev => prev.filter(l => l.id !== lineId));
                        }, TTL_MS);
                    }
                }
            } catch {}
        });

        await room.connect(url, token);

        // Publicar audio local — con procesamiento de voz activado para
        // evitar eco/acoplamiento cuando los altavoces están cerca del
        // micrófono (típico en móviles y laptops). El navegador implementa
        // estos filtros en su stack de audio nativo (WebRTC):
        //   - echoCancellation: cancela el audio remoto que vuelve al micro
        //   - noiseSuppression: elimina ruido de fondo (ventilador, calle)
        //   - autoGainControl: normaliza volumen del hablante
        let localAudioTrack;
        try {
            localAudioTrack = await createLocalAudioTrack({
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            });
            await room.localParticipant.publishTrack(localAudioTrack);
        } catch (err) {
            console.error('[calls] No se pudo publicar audio:', err.message);
        }

        // Arrancar la captura para subtítulos — usa el MISMO MediaStream
        // del micro que LiveKit ya abrió (no pide acceso otra vez).
        // El handshake con el backend es por ACK del socket — si el
        // backend no tiene Deepgram configurado o el idioma no es
        // streamable, simplemente no iniciamos el audio capture.
        if (subtitleOn && localAudioTrack && callId) {
            try {
                const socket = typeof getSocket === 'function' ? getSocket() : null;
                const ms = localAudioTrack.mediaStream
                    || new MediaStream([localAudioTrack.mediaStreamTrack]);
                if (socket && ms) {
                    console.log('[subtitles] arrancando sesión para callId:', callId);
                    socket.emit(
                        'call:subtitles:start',
                        { callId },
                        async (ack) => {
                            console.log('[subtitles] ACK startSession:', ack);
                            if (!ack?.ok) {
                                console.warn('[subtitles] no iniciados:', ack?.reason);
                                return;
                            }
                            try {
                                audioCaptureRef.current = await startAudioCapture({
                                    stream: ms,
                                    onChunk: (buf) => {
                                        // Solo emitir si el socket está conectado.
                                        // Si está intentando reconectar, descartamos
                                        // los chunks — si no, socket.io los acumula
                                        // en buffer y supera maxHttpBufferSize → 400.
                                        if (!socket.connected) return;
                                        try { socket.emit('call:subtitles:audio', buf); } catch {}
                                    }
                                });
                                console.log('[subtitles] captura de audio iniciada');
                            } catch (err) {
                                console.error('[subtitles] startAudioCapture falló:', err.message);
                            }
                        }
                    );
                }
            } catch (err) {
                console.warn('[subtitles] fallo al arrancar captura:', err.message);
            }
        } else if (subtitleOn && !callId) {
            console.warn('[subtitles] no se pasó callId a connectToRoom');
        }

        // Publicar video local (solo si type=video)
        if (type === 'video') {
            try {
                const videoTrack = await createLocalVideoTrack({
                    facingMode: 'user',
                    resolution: VideoPresets.h720.resolution
                });
                await room.localParticipant.publishTrack(videoTrack);
                setLocalVideoTrack(videoTrack);
                setCameraFacing('user');
            } catch (err) {
                console.error('[calls] No se pudo publicar video:', err.message);
            }
        }

        elapsedTimerRef.current = setInterval(() => {
            setElapsed(e => e + 1);
        }, 1000);

        setState(S_ACTIVE);
    }, [resetToIdle]);

    // ──────────────── Iniciar llamada (caller) ─────────────────
    const startOutgoing = useCallback(async ({ calleeId, type, name, avatar, groupId }) => {
        if (state !== S_IDLE) {
            alert(pickCallL().callInProgress);
            return;
        }
        if (!enabled) {
            alert(pickCallL().callsUnavailable);
            return;
        }
        const isGroup = !!groupId;
        setCallInfo({
            callId: null,
            type,
            peer: { id: calleeId, name, avatar },
            direction: 'outgoing',
            isGroup,
            groupId: groupId || null,
            groupName: isGroup ? (name || 'Grupo') : null
        });
        setState(S_OUTGOING);
        // Ringback al caller mientras espera respuesta del callee.
        try { stopRingbackRef.current = playRingback(); } catch {}
        try {
            const { data } = await callsInitiate(calleeId, type, groupId);
            setCallInfo(prev => ({
                ...prev,
                callId: data.callId,
                roomName: data.roomName,
                token: data.token,
                url: data.url,
                isGroup: !!data.isGroup,
                groupName: data.groupName || prev?.groupName,
                participantCount: data.invitedCount || 0
            }));
            // Si es grupal, conectamos a la sala YA (no esperamos a que nadie
            // acepte). Empezamos como "esperando a que entren los demás".
            if (data.isGroup && data.token && data.url) {
                setState(S_CONNECTING);
                connectToRoomRef.current({
                    url: data.url,
                    token: data.token,
                    type,
                    callId: data.callId
                }).catch(() => resetToIdle());
            }
        } catch (err) {
            // Caso especial: el callee está en otra llamada → backend
            // devuelve 409 con error='busy'. Sonamos tono de comunicando
            // y mostramos mensaje al caller.
            const errBody = err.response?.data;
            if (err.response?.status === 409 && errBody?.error === 'busy') {
                resetToIdle();
                playBusyTone(3000);
                setBusyNotice({
                    name: errBody.callee?.name || name || 'El usuario',
                    at: Date.now()
                });
                return;
            }
            console.error('[calls] initiate error:', err);
            alert(errBody?.error || pickCallL().cantStartCall);
            resetToIdle();
        }
    }, [state, enabled, resetToIdle]);

    // ──────────────── Aceptar llamada (callee) ─────────────────
    // Bug #4: pasamos a S_CONNECTING inmediatamente para deshabilitar el
    // botón — evita doble-click y sobre todo evita que el botón rojo de
    // colgar aparezca en la misma posición antes de que la conexión
    // termine y el usuario lo pulse por error creyendo que es aceptar.
    const acceptCall = useCallback(async () => {
        if (!callInfo || state !== S_INCOMING) return;
        setState(S_CONNECTING);
        // Parar ringtone/vibración en cuanto aceptamos
        if (stopRingtoneRef.current) { stopRingtoneRef.current(); stopRingtoneRef.current = null; }
        if (stopVibrateRef.current) { stopVibrateRef.current(); stopVibrateRef.current = null; }
        try {
            const { data } = await callsAccept(callInfo.callId);
            await connectToRoom({
                url: data.url,
                token: data.token,
                type: data.type,
                callId: data.callId || callInfo.callId
            });
        } catch (err) {
            console.error('[calls] accept error:', err);
            alert(err.response?.data?.error || pickCallL().cantAccept);
            resetToIdle();
        }
    }, [callInfo, state, connectToRoom, resetToIdle]);

    const declineCall = useCallback(async () => {
        if (!callInfo) return;
        try { await callsDecline(callInfo.callId); } catch {}
        resetToIdle();
    }, [callInfo, resetToIdle]);

    const cancelCall = useCallback(async () => {
        if (!callInfo?.callId) { resetToIdle(); return; }
        try { await callsCancel(callInfo.callId); } catch {}
        resetToIdle();
    }, [callInfo, resetToIdle]);

    const handleHangup = useCallback(async () => {
        if (!callInfo?.callId) { resetToIdle(); return; }
        try { await callsEnd(callInfo.callId); } catch {}
        resetToIdle();
    }, [callInfo, resetToIdle]);

    // ──────────────── Controles ─────────────────────────────────
    const toggleMute = useCallback(async () => {
        const room = roomRef.current;
        if (!room) return;
        const next = !muted;
        try {
            await room.localParticipant.setMicrophoneEnabled(!next);
            setMuted(next);
        } catch (err) {
            console.error('[calls] toggleMute:', err);
        }
    }, [muted]);

    const toggleCamera = useCallback(async () => {
        const room = roomRef.current;
        if (!room || callInfo?.type !== 'video') return;
        const next = !cameraOff;
        try {
            await room.localParticipant.setCameraEnabled(!next);
            setCameraOff(next);
        } catch (err) {
            console.error('[calls] toggleCamera:', err);
        }
    }, [cameraOff, callInfo]);

    // Bug #5: flip cámara fiable. En vez de leer getSettings() (a veces
    // undefined) llevamos el state local y alternamos explícitamente.
    // Además, en caso de que restartTrack falle, hacemos fallback a
    // republicar la pista (más compatible con WebView Android).
    const flipCamera = useCallback(async () => {
        const room = roomRef.current;
        if (!room || callInfo?.type !== 'video' || !localVideoTrack) return;
        const nextFacing = cameraFacing === 'user' ? 'environment' : 'user';
        try {
            await localVideoTrack.restartTrack({ facingMode: { exact: nextFacing } });
            setCameraFacing(nextFacing);
            return;
        } catch (err) {
            console.warn('[calls] restartTrack falló, intentando republicar:', err.message);
        }
        // Fallback: unpublish + createLocalVideoTrack + publish
        try {
            try { await room.localParticipant.unpublishTrack(localVideoTrack); } catch {}
            try { localVideoTrack.stop(); } catch {}
            const newTrack = await createLocalVideoTrack({
                facingMode: nextFacing,
                resolution: VideoPresets.h720.resolution
            });
            await room.localParticipant.publishTrack(newTrack);
            setLocalVideoTrack(newTrack);
            setCameraFacing(nextFacing);
        } catch (err) {
            console.error('[calls] flipCamera fallback también falló:', err.message);
            alert(pickCallL().cantSwitchCamera);
        }
    }, [callInfo, localVideoTrack, cameraFacing]);

    // ──────────────── Socket listeners ────────────────────────
    // Refs con el estado actual — permite que los handlers lean valores
    // frescos sin tener que re-registrarse en cada cambio de state/callInfo.
    // Antes el useEffect tenía [state, callInfo, ...] como deps, así que
    // cada cambio desregistraba y volvía a registrar. En la ventana del
    // cleanup se podían perder eventos — raro pero posible. Ahora el
    // efecto se monta UNA vez y los listeners permanecen vivos toda la
    // sesión.
    const stateRef = useRef(state);
    const callInfoRef = useRef(callInfo);
    const connectToRoomRef = useRef(connectToRoom);
    const resetToIdleRef = useRef(resetToIdle);
    useEffect(() => { stateRef.current = state; }, [state]);
    useEffect(() => { callInfoRef.current = callInfo; }, [callInfo]);
    useEffect(() => { connectToRoomRef.current = connectToRoom; }, [connectToRoom]);
    useEffect(() => { resetToIdleRef.current = resetToIdle; }, [resetToIdle]);

    useEffect(() => {
        const onIncoming = (data) => {
            if (stateRef.current !== S_IDLE) return;
            setCallInfo({
                callId: data.callId,
                type: data.type,
                peer: {
                    id: data.caller?.id,
                    name: data.caller?.name,
                    avatar: data.caller?.avatar
                },
                direction: 'incoming',
                isGroup: !!data.isGroup,
                groupId: data.groupId || null,
                groupName: data.groupName || null,
                participantCount: data.participantCount || 0
            });
            setState(S_INCOMING);
            try { stopRingtoneRef.current = playRingtone(); } catch {}
            try { stopVibrateRef.current = startVibratePattern(); } catch {}
        };

        const onAccepted = (data) => {
            setCallInfo(prev => {
                if (!prev || prev.callId !== data.callId) return prev;

                // Multi-dispositivo: si soy el receptor (state INCOMING)
                // y la llamada se aceptó en OTRO dispositivo del mismo user
                // (no puede haber sido este, porque de haberlo sido ya estaría
                // en CONNECTING/ACTIVE), parar ringtone+vibración y resetear.
                if (stateRef.current === S_INCOMING) {
                    try { if (stopRingtoneRef.current) stopRingtoneRef.current(); } catch {}
                    try { if (stopVibrateRef.current) stopVibrateRef.current(); } catch {}
                    stopRingtoneRef.current = null;
                    stopVibrateRef.current = null;
                    // Resetear fuera del setState para no anidar updates
                    setTimeout(() => resetToIdleRef.current(), 0);
                    return prev;
                }

                // Caso normal: yo soy el caller y el callee aceptó. Paramos
                // el ringback y conectamos al room.
                if (stopRingbackRef.current) {
                    try { stopRingbackRef.current(); } catch {}
                    stopRingbackRef.current = null;
                }
                if (prev.token && prev.url) {
                    setState(S_CONNECTING);
                    connectToRoomRef.current({
                        url: prev.url,
                        token: prev.token,
                        type: prev.type,
                        callId: prev.callId
                    }).catch(() => resetToIdleRef.current());
                }
                return prev;
            });
        };

        const onDeclined = (data) => {
            if (callInfoRef.current && callInfoRef.current.callId === data.callId) resetToIdleRef.current();
        };
        const onCanceled = (data) => {
            if (callInfoRef.current && callInfoRef.current.callId === data.callId) resetToIdleRef.current();
        };
        const onEnded = (data) => {
            if (callInfoRef.current && callInfoRef.current.callId === data.callId) resetToIdleRef.current();
        };
        const onMissed = (data) => {
            if (callInfoRef.current && callInfoRef.current.callId === data.callId) resetToIdleRef.current();
        };

        // Registrar listeners en un socket dado. Devuelve función de
        // cleanup. Necesario porque el socket puede no estar listo al
        // montar CallManager, o puede reconectar y necesitar re-registro.
        const attachTo = (s) => {
            if (!s) return null;
            s.on('incoming_call', onIncoming);
            s.on('call_accepted', onAccepted);
            s.on('call_declined', onDeclined);
            s.on('call_canceled', onCanceled);
            s.on('call_ended', onEnded);
            s.on('call_missed', onMissed);
            return () => {
                try {
                    s.off('incoming_call', onIncoming);
                    s.off('call_accepted', onAccepted);
                    s.off('call_declined', onDeclined);
                    s.off('call_canceled', onCanceled);
                    s.off('call_ended', onEnded);
                    s.off('call_missed', onMissed);
                } catch {}
            };
        };

        // Registra si el socket YA está disponible
        let currentCleanup = null;
        const trySetup = () => {
            const s = typeof getSocket === 'function' ? getSocket() : null;
            if (!s) return false;
            if (currentCleanup) currentCleanup();
            currentCleanup = attachTo(s);
            return true;
        };
        trySetup();

        // Re-registra cuando el socket esté listo o reconecte — App.js
        // dispara 'socket_ready' al primer connect y podríamos montar
        // CallManager antes de que eso ocurra.
        const onSocketReady = () => trySetup();
        window.addEventListener('socket_ready', onSocketReady);

        return () => {
            window.removeEventListener('socket_ready', onSocketReady);
            if (currentCleanup) currentCleanup();
        };
    }, []);  // solo en mount — listeners permanentes durante la sesión

    // ──────────────── Event DOM ────────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            const { calleeId, type, name, avatar, groupId } = e.detail || {};
            // En llamada grupal basta con groupId (no hace falta calleeId).
            if (!calleeId && !groupId) return;
            startOutgoing({ calleeId, type: type || 'voice', name, avatar, groupId });
        };
        window.addEventListener('kaixo:call:start', handler);
        return () => window.removeEventListener('kaixo:call:start', handler);
    }, [startOutgoing]);

    useEffect(() => {
        return () => teardown();
    }, [teardown]);

    // Cuando hay una llamada en curso (cualquier estado salvo IDLE) escuchamos
    // 'new_message' para contar mensajes recibidos y mostrar un mini-aviso al
    // usuario. Al volver a IDLE reseteamos el contador.
    useEffect(() => {
        if (state === S_IDLE) {
            // Reset al colgar — los unreadCount reales viven en ConversationList.
            if (missedMsgs.count !== 0) setMissedMsgs({ count: 0, lastFrom: '' });
            return;
        }
        const sock = getSocket?.();
        if (!sock) return;
        const handler = (msg) => {
            // Ignoramos mensajes que enviamos nosotros (echo)
            if (!msg || String(msg.sender?._id || msg.sender) === String(currentUser?._id)) return;
            setMissedMsgs(prev => ({
                count: prev.count + 1,
                lastFrom: msg.sender?.name || msg.senderName || ''
            }));
        };
        sock.on('new_message', handler);
        sock.on('new_group_message', handler);
        return () => {
            sock.off('new_message', handler);
            sock.off('new_group_message', handler);
        };
    }, [state, currentUser, missedMsgs.count]);

    // ──────────────── Render ───────────────────────────────────
    // Si estamos en IDLE pero hay un aviso de busy activo, lo mostramos
    // como banner flotante. En cualquier otro caso de IDLE no renderizamos.
    if (state === S_IDLE) {
        if (!busyNotice) return null;
        const Lc = pickCallL();
        return ReactDOM.createPortal(
            <div className="call-busy-banner">
                <div className="call-busy-icon">📞</div>
                <div>
                    <div className="call-busy-title">{Lc.userBusyTitle(busyNotice.name)}</div>
                    <div className="call-busy-sub">{Lc.userBusySub}</div>
                </div>
                <button className="call-busy-close" onClick={() => setBusyNotice(null)}>✕</button>
            </div>,
            document.body
        );
    }

    const fmtElapsed = (s) => {
        const m = Math.floor(s / 60);
        const ss = String(s % 60).padStart(2, '0');
        return `${m}:${ss}`;
    };

    const peer = callInfo?.peer || {};
    const peerAvatar = peer.avatar
        ? <img src={peer.avatar} alt="" />
        : <span>{(peer.name || '?')[0].toUpperCase()}</span>;

    const isVideoCall = callInfo?.type === 'video';
    const isGroupCall = !!callInfo?.isGroup;
    // Vista "pantalla de video" cuando está conectando Y es de video,
    // o cuando ya está activa. Cuando es voz mantenemos la pantalla
    // con avatar grande centrado.
    const showVideoLayout = isVideoCall && (state === S_ACTIVE || state === S_CONNECTING);
    const showGroupGrid = isGroupCall && isVideoCall && (state === S_ACTIVE || state === S_CONNECTING);
    // Convertimos el Map de participantes a array ordenado por identity
    const participantsList = Array.from(remoteParticipants.values());

    return ReactDOM.createPortal(
        <div className={`call-manager call-${state} call-${callInfo?.type || 'voice'}`}>
            {/* audio remoto siempre montado — oculto */}
            <audio ref={remoteAudioElRef} autoPlay playsInline style={{ display: 'none' }} />

            {/* Aviso flotante: mensajes recibidos durante la llamada */}
            {missedMsgs.count > 0 && (
                <div className="call-msg-pulse" key={missedMsgs.count}>
                    <span className="call-msg-pulse-dot">{missedMsgs.count}</span>
                    <span className="call-msg-pulse-text">
                        {missedMsgs.count === 1
                            ? `Mensaje de ${missedMsgs.lastFrom || 'un contacto'}`
                            : `${missedMsgs.count} mensajes nuevos`}
                    </span>
                </div>
            )}

            {showVideoLayout && !showGroupGrid && (
                <>
                    <video
                        ref={remoteVideoElRef}
                        className="call-remote-video"
                        autoPlay
                        playsInline
                        controls={false}
                        disablePictureInPicture
                    />
                    <video
                        ref={localVideoElRef}
                        className="call-local-video"
                        autoPlay
                        playsInline
                        muted
                        controls={false}
                        disablePictureInPicture
                    />
                </>
            )}

            {/* Grid de participantes en llamada grupal de video */}
            {showGroupGrid && (
                <div className={`call-grid call-grid-${Math.min(participantsList.length + 1, 6)}`}>
                    <CallGridTile
                        local
                        videoTrack={localVideoTrack}
                        videoElRef={localVideoElRef}
                        name="Tú"
                        muted
                    />
                    {participantsList.map(p => (
                        <CallGridTile
                            key={p.id}
                            videoTrack={p.videoTrack}
                            audioTrack={p.audioTrack}
                            name={p.name}
                        />
                    ))}
                </div>
            )}

            {/* Info del peer — visible salvo en video activo */}
            {(!showVideoLayout) && (
                <div className="call-peer-info">
                    <div className={`call-peer-avatar ${state === S_INCOMING || state === S_OUTGOING ? 'is-ringing' : ''}`}>{peerAvatar}</div>
                    <div className="call-peer-name">{peer.name || '—'}</div>
                    <div className="call-peer-status">
                        {state === S_OUTGOING && 'Llamando…'}
                        {state === S_INCOMING && `Llamada entrante de ${isVideoCall ? 'video' : 'voz'}`}
                        {state === S_CONNECTING && 'Conectando…'}
                        {state === S_ACTIVE && fmtElapsed(elapsed)}
                    </div>
                </div>
            )}

            {showVideoLayout && state === S_ACTIVE && (
                <div className="call-video-timer">{fmtElapsed(elapsed)}</div>
            )}
            {showVideoLayout && state === S_CONNECTING && (
                <div className="call-video-timer">{pickCallL().connecting}</div>
            )}

            {/* Subtítulos en vivo: últimas N frases apiladas, cada una
                con TTL propio. La más reciente abajo, las antiguas van
                saliendo por arriba con fade.
                Líneas "pending": franja tenue con 3 puntos animados
                mientras se transcribe + traduce. Al llegar el texto
                real, la misma franja se llena y se vuelve opaca. */}
            {state === S_ACTIVE && subtitleOn && subtitleLines.length > 0 && (
                <div className="call-subtitles">
                    {subtitleLines.map(line => (
                        <div
                            key={line.id}
                            className={`call-subtitle-line ${line.pending ? 'is-pending' : ''}`}
                        >
                            {line.pending ? (
                                // Dots con SVG puro: imposible que se deformen
                                // por flex/inline/line-height. Los círculos
                                // quedan perfectos en cualquier navegador.
                                <span className="call-subtitle-pending">
                                    <svg width="54" height="12" viewBox="0 0 54 12" aria-hidden="true">
                                        <circle className="dot" cx="5" cy="6" r="3" />
                                        <circle className="dot" cx="16" cy="6" r="3" />
                                        <circle className="dot" cx="27" cy="6" r="3" />
                                        <circle className="dot" cx="38" cy="6" r="3" />
                                        <circle className="dot" cx="49" cy="6" r="3" />
                                    </svg>
                                </span>
                            ) : (
                                <span>
                                    {/* En llamada grupal mostramos el nombre del speaker
                                        delante de cada subtítulo para distinguir quién
                                        dijo qué cuando hablan varias personas. */}
                                    {isGroupCall && line.speakerName && (
                                        <strong className="call-subtitle-speaker">{line.speakerName}: </strong>
                                    )}
                                    {line.text}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div className="call-controls">
                {state === S_INCOMING && (
                    <>
                        <button className="call-btn call-btn-decline" onClick={declineCall} aria-label="Rechazar">
                            <HangupIcon />
                        </button>
                        <button className="call-btn call-btn-accept" onClick={acceptCall} aria-label="Aceptar">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                        </button>
                    </>
                )}

                {state === S_CONNECTING && (
                    <button className="call-btn call-btn-decline" onClick={handleHangup} aria-label="Cancelar">
                        <HangupIcon />
                    </button>
                )}

                {state === S_OUTGOING && (
                    <button className="call-btn call-btn-decline" onClick={cancelCall} aria-label="Cancelar">
                        <HangupIcon />
                    </button>
                )}

                {state === S_ACTIVE && (
                    <>
                        <button
                            className={`call-btn call-btn-secondary ${muted ? 'is-active' : ''}`}
                            onClick={toggleMute}
                            aria-label="Silenciar"
                            title={muted ? 'Activar micro' : 'Silenciar'}
                        >
                            {muted
                                ? <Icons.MicrophoneOff size={22} color="white" />
                                : <Icons.Microphone size={22} color="white" />}
                        </button>
                        <button
                            className={`call-btn call-btn-secondary ${subtitleOn ? 'is-active' : ''}`}
                            onClick={() => setSubtitleOn(v => !v)}
                            aria-label="Subtítulos"
                            title={subtitleOn ? 'Subtítulos activados' : 'Subtítulos desactivados'}
                        >
                            <Icons.Subtitles size={22} color="white" />
                        </button>
                        <button
                            className="call-btn call-btn-secondary"
                            onClick={() => setShowInviteModal(true)}
                            aria-label="Invitar a más personas"
                            title="Invitar a más personas"
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <line x1="20" y1="8" x2="20" y2="14" />
                                <line x1="17" y1="11" x2="23" y2="11" />
                            </svg>
                        </button>
                        {isVideoCall && (
                            <>
                                <button
                                    className={`call-btn call-btn-secondary ${cameraOff ? 'is-active' : ''}`}
                                    onClick={toggleCamera}
                                    aria-label="Cámara"
                                    title={cameraOff ? 'Activar cámara' : 'Apagar cámara'}
                                >
                                    {cameraOff
                                        ? <Icons.VideoCameraOff size={22} color="white" />
                                        : <Icons.VideoCamera size={22} color="white" />}
                                </button>
                                <button
                                    className="call-btn call-btn-secondary"
                                    onClick={flipCamera}
                                    aria-label="Cambiar cámara"
                                    title="Cambiar cámara"
                                >
                                    <Icons.CameraSwitch size={22} color="white" />
                                </button>
                            </>
                        )}
                        <button className="call-btn call-btn-decline" onClick={handleHangup} aria-label="Colgar">
                            <HangupIcon />
                        </button>
                    </>
                )}
            </div>
            {showInviteModal && callInfo?.callId && (
                <CallInviteModal
                    callId={callInfo.callId}
                    currentUser={currentUser}
                    excludeIds={[
                        currentUser?._id,
                        ...(callInfo?.peer?.id ? [callInfo.peer.id] : []),
                        ...Array.from(remoteParticipants.keys())
                    ].filter(Boolean).map(String)}
                    onClose={() => setShowInviteModal(false)}
                />
            )}
        </div>,
        document.body
    );
}

// ─────────────────────────────────────────────────────────────────────
// Modal "Invitar a más personas a la llamada en curso".
// Lista los contactos del usuario que tienen KAIXO (de su agenda
// sincronizada en backend), permite seleccionar varios con checkbox,
// y al pulsar "Invitar" llama al endpoint POST /calls/:id/invite.
// ─────────────────────────────────────────────────────────────────────
function CallInviteModal({ callId, currentUser, excludeIds = [], onClose }) {
    const [contacts, setContacts] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        let cancelled = false;
        const apiGetContacts = require('../../services/api').getContacts;
        apiGetContacts(currentUser._id)
            .then(res => {
                if (cancelled) return;
                const wk = res?.data?.contacts?.withKaixo || [];
                const list = wk
                    .map(c => ({
                        id: String(c.kaixoUser?.id || ''),
                        name: c.kaixoUser?.name || c.name,
                        avatar: c.kaixoUser?.avatar || ''
                    }))
                    .filter(c => c.id && !excludeIds.includes(c.id));
                setContacts(list);
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?._id]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return contacts;
        return contacts.filter(c => (c.name || '').toLowerCase().includes(q));
    }, [contacts, search]);

    const toggle = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleInvite = async () => {
        if (selected.size === 0) return;
        setBusy(true);
        setMsg('');
        try {
            const userIds = Array.from(selected);
            await callsInvite(callId, userIds);
            setMsg(`✓ Invitación enviada a ${userIds.length} ${userIds.length === 1 ? 'persona' : 'personas'}`);
            setTimeout(() => onClose(), 900);
        } catch (e) {
            setMsg(e?.response?.data?.error || 'No se pudo invitar');
        } finally {
            setBusy(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="call-invite-overlay" onClick={onClose}>
            <div className="call-invite-modal" onClick={e => e.stopPropagation()}>
                <div className="call-invite-head">
                    <h3>{pickCallL().inviteTitle}</h3>
                    <button className="call-invite-close" onClick={onClose}>✕</button>
                </div>
                <input
                    type="text"
                    className="call-invite-search"
                    placeholder={pickCallL().searchContact}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                {loading && <div className="call-invite-empty">{pickCallL().loadingContacts}</div>}
                {!loading && filtered.length === 0 && (
                    <div className="call-invite-empty">
                        {contacts.length === 0
                            ? 'Aún no tienes contactos con KAIXO en tu agenda.'
                            : 'Sin resultados.'}
                    </div>
                )}
                {!loading && filtered.length > 0 && (
                    <div className="call-invite-list">
                        {filtered.map(c => {
                            const checked = selected.has(c.id);
                            return (
                                <label key={c.id} className={`call-invite-row ${checked ? 'checked' : ''}`}>
                                    <input type="checkbox" checked={checked} onChange={() => toggle(c.id)} />
                                    <div className="call-invite-avatar">
                                        {c.avatar
                                            ? <img src={c.avatar} alt="" />
                                            : <span>{(c.name || '?')[0].toUpperCase()}</span>}
                                    </div>
                                    <span className="call-invite-name">{c.name}</span>
                                </label>
                            );
                        })}
                    </div>
                )}
                {msg && <div className="call-invite-msg">{msg}</div>}
                <div className="call-invite-foot">
                    <button className="call-invite-cancel" onClick={onClose} disabled={busy}>
                        Cancelar
                    </button>
                    <button
                        className="call-invite-confirm"
                        onClick={handleInvite}
                        disabled={busy || selected.size === 0}
                    >
                        {busy ? 'Invitando…' : `Invitar (${selected.size})`}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

// ─────────────────────────────────────────────────────────────────────
// Tile individual de un participante en una llamada grupal de video.
// Adjunta su videoTrack/audioTrack a elementos propios y muestra nombre.
// ─────────────────────────────────────────────────────────────────────
function CallGridTile({ videoTrack, audioTrack, name, local = false, muted = false, videoElRef }) {
    const ownVideoRef = useRef(null);
    const audioRef = useRef(null);

    useEffect(() => {
        const target = videoElRef ? videoElRef.current : ownVideoRef.current;
        if (videoTrack && target) {
            try { videoTrack.attach(target); } catch {}
        }
        return () => {
            if (videoTrack && target) {
                try { videoTrack.detach(target); } catch {}
            }
        };
    }, [videoTrack, videoElRef]);

    useEffect(() => {
        if (!audioTrack || !audioRef.current) return;
        try { audioTrack.attach(audioRef.current); } catch {}
        applyRemoteVolumeBoost(audioRef.current);
        return () => {
            try { audioTrack.detach(audioRef.current); } catch {}
        };
    }, [audioTrack]);

    return (
        <div className={`call-grid-tile ${local ? 'call-grid-tile-local' : ''} ${!videoTrack ? 'call-grid-tile-novideo' : ''}`}>
            {videoTrack ? (
                <video
                    ref={videoElRef || ownVideoRef}
                    autoPlay
                    playsInline
                    muted={muted || local}
                    controls={false}
                    disablePictureInPicture
                />
            ) : (
                <div className="call-grid-tile-fallback">
                    <span>{(name || '?')[0].toUpperCase()}</span>
                </div>
            )}
            {!local && audioTrack && (
                <audio ref={audioRef} autoPlay playsInline />
            )}
            <span className="call-grid-tile-name">{name || ''}</span>
        </div>
    );
}
